import { DynamicFormProvider } from "@scenes/bulk-request/context/DynamicFormContext";
import { SelectedItemsProvider } from "../../contexts/SelectedItemsContext";
import { Platform, ScrollView } from "react-native";
import { SpaceType } from "@components/atoms/Spacer/types";
import Container from "@components/atoms/Container";
import Spacer from "@components/atoms/Spacer";
import RequestStatusCard from "./RequestStatusCard";
import CompanyDetailsCard from "./utils/CompanyDetailsCard";
import StoreCustomerAttributes from "./StoreAttributesCard";
import CommentSection from "./CommentSection";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBText } from "@components/atoms";
import { ProductCategory, ProductTypeCommon, ViewType } from "@utils/constants";
import RequestViewParentCard from "@components/organisms/RequestViewParentCard";
import { getBoxShadow } from "@utils/product/productValidators";
import TotalPriceCard from "./TotalPriceCard";
import styled from "styled-components/native";
import { DefaultThemeValueType } from "../../../styled";
import { parseOrderComments } from "./utils/utils";
import React from "react";
import { SearchFilterTypeEnumLowercase } from "@components/atoms/Filters/utils";
import PaymentDetails from "./utils/PaymentDetails";
import { useUnifiedProductPagesSlice } from "@redux/slices/unifiedProductPages";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import ShippingLocation from "./utils/ShippingLocationRequest";
import { useTranslation } from "react-i18next";
import { useOrderCart } from "@redux/slices/globalSlice";
import ShippingInformation from "./utils/ShippingInformation";
import { extractWireTransferSection } from "@scenes/configurator/OrderCart/DynamicSuccessPopUp";
import { getProductName } from "@utils/product/productNames";
import { handleDeepLink } from "@utils/product/productFormatters";
import LineViewContainer from "./utils/LineView";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import SalesPersonButtonsContainer from "./utils/SalesPersonButtonsContainer";

type OrderRequestScreenProps = {
  requestName?: string;
  theme?: DefaultThemeValueType;
  stockItemRequest?: any;
  isCustomApp: boolean;
  showCompanyDetails?: any;
  productType?: ProductCategory;
  iconData?: any;
  typography?: any;
  validValue?: any;
  productPrefix?: string;
  hasChildren?: boolean;
  setDisabledButton?: any;
  modifyRequest?: () => void;
  disabledButton?: boolean;
  selectedItems?: any;
  isHideOtherButtonVisible?: boolean;
  isBuilderItem?: boolean;
};

const OrderRequestScreen = ({
  requestName,
  theme,
  stockItemRequest,
  isCustomApp,
  showCompanyDetails,
  productType,
  iconData,
  typography,
  validValue,
  hasChildren,
  setDisabledButton,
  modifyRequest,
  disabledButton = false,
  selectedItems,
  isHideOtherButtonVisible,
  isBuilderItem,
}: OrderRequestScreenProps) => {
  const storeCustomerAttributes = parseOrderComments(
    stockItemRequest?.order_comments
  );
  const isLooseParcel = stockItemRequest?.[productType]?.diamond_category
    ? stockItemRequest?.[productType]?.diamond_category ===
      SearchFilterTypeEnumLowercase.LOOSE_PARCEL
    : false;
  const unifiedProductPagesSlice = useUnifiedProductPagesSlice();
  const userInfo = useUserInfo();
  const orderCart = useOrderCart();
  const priceMode = userInfo?.user?.price_mode;
  const paymentDetails =
    unifiedProductPagesSlice?.unifiedProductData?.[
      productType?.productType
    ]?.requests?.payment_details?.price_modes.includes(priceMode);
  const { t } = useTranslation();
  const shippingInformation = stockItemRequest?.shipping_instructions;
  const wireTransferConfig = orderCart?.successOfflinePopupConfig;
  const wireTransferDetails = extractWireTransferSection(
    wireTransferConfig,
    stockItemRequest?.wire_transfer_details
  );
  const isSalesPerson =
    !userInfo.forceGreenPriceProfile() &&
    userInfo.priceProfileVisibility() &&
    !userInfo.isPriceModeLock() &&
    priceMode === PriceModes.ORANGE;

  const showButtons =
    userInfo?.getFeatureSettings()?.enable_order_cart_approval_workflow ===
      true &&
    isSalesPerson &&
    stockItemRequest?.view_type === ViewType.SELLER;

  const hideBecauseMemo =
    stockItemRequest?.memo_status &&
    stockItemRequest?.view_type === ViewType.BUYER;

  const renderButtons = () => (
    <StyledButtonsContainer
      stockItemRequest={stockItemRequest}
      productType={productType}
      disabledButton={disabledButton}
      modifyRequest={modifyRequest}
      selectedItems={selectedItems}
      isHideOtherButtonVisible={isHideOtherButtonVisible}
      isBuilderItem={isBuilderItem}
    />
  );

  return (
    <SelectedItemsProvider>
      <DynamicFormProvider>
        <MainContainer>
          <ScrollView
            style={{
              paddingHorizontal: theme.isMobile
                ? SpaceType.Basic
                : SpaceType.Double,
            }}
          >
            <Container maxWidth={1008} noPadding>
              <Spacer size={SpaceType.Double} />
              <RequestStatusCard
                stockItemRequest={stockItemRequest}
                isCustomApp={isCustomApp}
                requestName={requestName}
              />
              <Spacer size={SpaceType.Double} />
              {showCompanyDetails && (
                <>
                  <CompanyDetailsCard
                    stockItemRequest={stockItemRequest}
                    productType={productType}
                  />
                  <Spacer size={SpaceType.Double} />
                </>
              )}
              {Object.keys(stockItemRequest?.payment_details || {}).length >
                0 &&
                paymentDetails && (
                  <>
                    <PaymentDetails
                      payment_details={stockItemRequest?.payment_details}
                      productType={productType?.productType}
                    />
                    <Spacer size={SpaceType.Double} />
                  </>
                )}
              {typeof storeCustomerAttributes?.attributes === "object" && (
                <StoreCustomerAttributes
                  attributes={storeCustomerAttributes?.attributes}
                />
              )}
              {Object.keys(stockItemRequest.shipping_location || {}).length >
                0 && (
                <>
                  <ShippingLocation
                    shipping_location={stockItemRequest.shipping_location}
                  />
                  <Spacer size={SpaceType.Double} />
                </>
              )}
              {shippingInformation && (
                <>
                  <ShippingInformation
                    title={t(
                      "requestHistoryDetail.shippingInformation.title.text",
                      "Shipping Information"
                    )}
                    description={shippingInformation}
                  />
                  <Spacer size={SpaceType.Double} />
                </>
              )}
              {wireTransferDetails?.description && (
                <>
                  <ShippingInformation
                    title={t(wireTransferDetails?.title)}
                    description={wireTransferDetails?.description}
                  />
                  <Spacer size={SpaceType.Double} />
                </>
              )}
              <CommentSection
                requestType={""}
                comments={storeCustomerAttributes?.comments}
              />
              <CardContainer>
                <ProductListTopContainer background={productType.theme}>
                  <VDBIcon
                    variant={IconsVariant.VDBCustom}
                    name={iconData}
                    size={SpaceType.Big}
                    color={theme[`${productType.theme}-06`]}
                  />
                  <Spacer size={SpaceType.ExtraSmall} />
                  <VDBText
                    type={typography["H3-M"]}
                    color={theme[`${productType.theme}-06`]}
                  >
                    {t(
                      stockItemRequest?.[productType.productType]?.sub_type ===
                        ProductTypeCommon.LABGROWN_JEWELRY_SUB_TYPE
                        ? getProductName(ProductCategory.LABGROWN_JEWELRY, t)
                        : stockItemRequest.type_text
                    )}
                  </VDBText>
                </ProductListTopContainer>
                <ItemsContainer>
                  <RequestViewParentCard
                    stockItemRequest={stockItemRequest}
                    itemList={stockItemRequest?.children}
                    productType={productType}
                    validValue={validValue}
                    onClickSupplierInfo={() =>
                      handleDeepLink(stockItemRequest?.deep_link ?? "")
                    }
                    setDisabledButton={setDisabledButton}
                    fromRequestDetail={true}
                    isModifyScreen={true}
                    orderCartRequest={true}
                  />
                </ItemsContainer>
              </CardContainer>
              <Spacer size={SpaceType.Double} />

              <TotalPriceCard
                stockItemRequest={stockItemRequest}
                productType={productType}
                hasChildren={hasChildren}
                orderCartRequest={true}
                isLooseParcel={isLooseParcel}
              />

              {(theme.isTablet || theme.isLargeScreen) &&
                !hideBecauseMemo &&
                showButtons && (
                  <>
                    {renderButtons()}
                    <Spacer size={SpaceType.Size_80} />
                  </>
                )}

              <Spacer size={SpaceType.ExtraLarge} />
            </Container>
          </ScrollView>

          {theme.isMobile && !hideBecauseMemo && showButtons && (
            <>
              <LineViewContainer background="core-03" marginLeft={false} />
              {renderButtons()}
            </>
          )}

          {Platform.OS === "ios" && <FooterSpace />}
        </MainContainer>
      </DynamicFormProvider>
    </SelectedItemsProvider>
  );
};

const MainContainer = styled.View`
  flex: 1;
  background-color: ${({ theme }) => theme["core-02"]};
`;

const FooterSpace = styled.View`
  padding: 10px;
  height: ${Platform.OS === "ios" ? 34 : 10}px;
  background-color: ${({ theme }) => theme["core-01"]};
`;

const CardContainer = styled.View`
  background-color: ${({ theme }) => theme["core-01"]};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme["core-03"]};
  box-shadow: ${({ theme }) => getBoxShadow(theme.Shadows.Gentle)};
`;

const ItemsContainer = styled.View`
  padding-bottom: ${SpaceType.Basic}px;
  padding-left: ${({ theme }) =>
    theme.isMobile ? `${SpaceType.Basic}px` : `${SpaceType.Double}px`};
  padding-right: ${({ theme }) =>
    theme.isMobile ? `${SpaceType.Basic}px` : `${SpaceType.Double}px`};
`;

const ProductListTopContainer = styled.View<{ background: string }>`
  background-color: ${({ background, theme }) => theme[`${background}-03`]};
  padding: ${SpaceType.Extra}px ${SpaceType.Extra}px;
  flex-direction: row;
  align-items: center;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
`;

const StyledButtonsContainer = styled(SalesPersonButtonsContainer)`
  border-radius: 8px;
  box-shadow: ${({ theme }) => getBoxShadow(theme.Shadows.Gentle)};
`;

export default OrderRequestScreen;
